import { SummaryGrant } from "../../slices/grants";
import { TableProperties } from "exceljs";
import {
  buildExcelTable,
  genEmptyRow,
  getChunksFromString,
  MaxCharsAllowed,
  getOrderedExportColumns,
  mapColumnsToExportFormat,
} from "../../services/exporter";
import { removeHtmlTags } from "../../services/dataParser";
import {
  DateFormat,
  formatDate,
  getNewDateNoTimezoneAdjustment,
} from "../../utils/conversion/date-converters";
import { ForecastRecord } from "../../utils/types/Forecast";
import axios from "axios";
import {
  createRequestWithAuthHeaders,
  getFullUrl,
} from "../../configs/axios-export.custom";
import { buildQuery } from "../../utils/Helpers/queryBuilder";
import { currencyFormatterNoDecimals } from "../USAidScreen/USAid";
import { format } from "date-fns";
import { DropdownOption } from "../../components/Widgets/Inputs/Input";
import { ColumnsSettingsData, DataColumnConfigProps } from "../../components/MaterialTable/Table";
import { 
  getUsaSpendingColumnsConfig,
  getForeignAssistanceColumnsConfig,
} from "./projectsTableColumns.config";

export const getSortableColumnPropertyName = (
  _propertyName: string,
): string => {
  return _propertyName;
};

export const getLabelForMechanism = (
  awardMechanism: string,
  mechanismsOptions: any[],
): string => {
  const mech = mechanismsOptions
    ? mechanismsOptions.find((m) => m.value == awardMechanism)
    : null;
  return mech ? mech.label : "";
};

export const getExportableDataTable = (
  data: any[],
  mechanismsOptions: any[],
  isFA: boolean,
  savedColumnSettings?: ColumnsSettingsData[] | null,
): TableProperties => {
  // Get default columns configuration based on isFA
  const defaultColumns = isFA 
    ? getForeignAssistanceColumnsConfig(getSortableColumnPropertyName)
    : getUsaSpendingColumnsConfig(getSortableColumnPropertyName);
  
  // Get ordered and filtered columns based on user settings
  const orderedColumns = getOrderedExportColumns(savedColumnSettings, defaultColumns);

  // Define data accessors - common fields first
  const commonAccessors: { [propertyName: string]: (record: any) => any } = {
    country: (record) => record.country,
    projectNumber: (record) => 
      record.projectNumber?.toLowerCase() === "null" ? "" : record.projectNumber,
    projectName: (record) => record.projectName,
    projectDescription: (record) => removeHtmlTags(record.projectDescription || ""),
    implementer: (record) => record.implementer,
  };

  // Foreign Assistance specific accessors
  const faAccessors: { [propertyName: string]: (record: any) => any } = {
    ...commonAccessors,
    technicalSector: (record) => record.technicalSector,
    managingAgency: (record) => record.managingAgency,
    fundingAgency: (record) => record.fundingAgency,
    fundingAccount: (record) => record.fundingAccount,
    activityStartDate: (record) =>
      !record.activityStartDate ||
      new Date(record.activityStartDate).toISOString().startsWith("0")
        ? "N/A"
        : format(getNewDateNoTimezoneAdjustment(record.activityStartDate), "MM/dd/yyyy"),
    activityEndDate: (record) =>
      !record.activityEndDate ||
      new Date(record.activityEndDate).toISOString().startsWith("0")
        ? "N/A"
        : format(getNewDateNoTimezoneAdjustment(record.activityEndDate), "MM/dd/yyyy"),
    obligations: (record) =>
      record.obligations !== null
        ? currencyFormatterNoDecimals.format(Math.round(record.obligations))
        : "",
    disbursements: (record) =>
      record.disbursements !== null
        ? currencyFormatterNoDecimals.format(Math.round(record.disbursements))
        : "",
  };

  // USA Spending specific accessors
  const usAccessors: { [propertyName: string]: (record: any) => any } = {
    ...commonAccessors,
    subawardRecipients: (record) => record.subawardRecipients ?? "",
    awardMechanism: (record) => getLabelForMechanism(record.awardMechanism, mechanismsOptions),
    awardingAgencyName: (record) => record.awardingAgencyName,
    awardingOffice: (record) => record.awardingOffice,
    parentIDVAgencyName: (record) => record.parentIDVAgencyName,
    setAside: (record) => record.setAside,
    naicsCode: (record) => record.naicsCode,
    startDate: (record) =>
      !record.startDate || new Date(record.startDate).toISOString().startsWith("0")
        ? "N/A"
        : format(getNewDateNoTimezoneAdjustment(record.startDate), "MM/dd/yyyy"),
    currentEndDate: (record) =>
      !record.currentEndDate || new Date(record.currentEndDate).toISOString().startsWith("0")
        ? "N/A"
        : format(getNewDateNoTimezoneAdjustment(record.currentEndDate), "MM/dd/yyyy"),
    potentialEndDate: (record) =>
      !record.potentialEndDate || new Date(record.potentialEndDate).toISOString().startsWith("0")
        ? "N/A"
        : format(getNewDateNoTimezoneAdjustment(record.potentialEndDate), "MM/dd/yyyy"),
    obligatedAmount: (record) =>
      record.obligatedAmount !== null
        ? currencyFormatterNoDecimals.format(Math.round(record.obligatedAmount))
        : "",
    currentAmount: (record) =>
      record.currentAmount !== null
        ? currencyFormatterNoDecimals.format(Math.round(record.currentAmount))
        : "",
    potentialAmount: (record) =>
      record.potentialAmount !== null
        ? currencyFormatterNoDecimals.format(Math.round(record.potentialAmount))
        : "",
    recipientAddress: (record) => record.recipientAddress,
  };

  // Select the appropriate accessors based on isFA
  const dataAccessors = isFA ? faAccessors : usAccessors;

  // Add Project Profile Link column
  const finalColumns = [
    ...orderedColumns,
    {
      propertyName: "projectProfileLink",
      label: "Project Profile Link",
      order: 9999,
      isVisible: true,
      sortable: false,
    } as DataColumnConfigProps,
  ];

  const finalAccessors = {
    ...dataAccessors,
    projectProfileLink: (record) =>
      isFA
        ? `${window.location.origin ?? "https://aidkonekt.com"}/project-view/record?projectNumber=${record?.projectNumber}&fa=true`
        : `${window.location.origin ?? "https://aidkonekt.com"}/project-view/record?id=${record.id}&fa=false`,
  };

  // Map columns and data to export format
  const { columns, rows } = mapColumnsToExportFormat(finalColumns, data, finalAccessors);

  return buildExcelTable("ProjectsExport", columns, rows);
};

export const getExcelDataUsaSpending = async (
  context,
  queryDto,
  totalItems: number = 1000,
): Promise<any[]> => {
  const q = queryDto;
  if (q) {
    q.pageSize = totalItems;
    q.pageIndex = 0;
  }
  let result = Array<any>();
  await axios
    .get(
      getFullUrl(`/api/usaspending${buildQuery(q, "|")}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    )
    .then((response) => {
      result = response.data.data;
    })
    .catch((error) => {
      alert("error fetching data");
    });
  return result;
};

export const getExcelDataForeignAssistance = async (
  context,
  queryDto,
  totalItems: number = 1000,
): Promise<any[]> => {
  const q = queryDto;
  if (q) {
    q.pageSize = totalItems;
    q.pageIndex = 0;
  }
  let result = Array<any>();
  await axios
    .get(
      getFullUrl(`/api/ForeignAssistance${buildQuery(q, "|")}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    )
    .then((response) => {
      result = response.data.data;
    })
    .catch((error) => {
      alert("error fetching data");
    });
  return result;
};

export const usaSpendingAgencies: DropdownOption[] = [
  {
    value: "Agency for International Development",
    label: "Agency for International Development",
  },
  { value: "Department of Defense", label: "Department of Defense" },
  {
    value: "Department of Health and Human Services",
    label: "Department of Health and Human Services",
  },
  { value: "Department of State", label: "Department of State" },
  {
    value: "Millennium Challenge Corporation",
    label: "Millennium Challenge Corporation",
  },
  {
    value: "Social Security Administration",
    label: "Social Security Administration",
  },
];

export const usaSpendingAwardingOffices: DropdownOption[] = [
  { value: "874665 BA78", label: "874665 BA78" },
  {
    value: "ACF OFFICE OF GOVERNMENT CONTRACTING SERVICES",
    label: "ACF OFFICE OF GOVERNMENT CONTRACTING SERVICES",
  },
  { value: "ACQUISITIONS", label: "ACQUISITIONS" },
  {
    value: "AGENCY FOR INTERNATIONAL DEVELOPMENT",
    label: "AGENCY FOR INTERNATIONAL DEVELOPMENT",
  },
  {
    value: "AHRQ/HEALTHCARE RESEARCH  QUALITY",
    label: "AHRQ/HEALTHCARE RESEARCH  QUALITY",
  },
  { value: "CDC - PITTSBURG", label: "CDC - PITTSBURG" },
  { value: "DCMA MIDDLE EAST", label: "DCMA MIDDLE EAST" },
  {
    value: "DEFENSE THREAT REDUCTION AGENCY",
    label: "DEFENSE THREAT REDUCTION AGENCY",
  },
  {
    value: "DEPT OF HHS/NATIONAL INSTITUTES FOR HEALTH",
    label: "DEPT OF HHS/NATIONAL INSTITUTES FOR HEALTH",
  },
  {
    value: "DIVISION OF ACQUISITIONS POLICY HQ",
    label: "DIVISION OF ACQUISITIONS POLICY HQ",
  },
  {
    value: "DIVISION OF PROCUREMENT SERVICES",
    label: "DIVISION OF PROCUREMENT SERVICES",
  },
  { value: "DLA HEADQUARTERS", label: "DLA HEADQUARTERS" },
  { value: "DOD EDUCATION ACTIVITY", label: "DOD EDUCATION ACTIVITY" },
  { value: "FORT BRAGG SOUTH", label: "FORT BRAGG SOUTH" },
  {
    value: "FY8606  US OFFICE LIAISON ADMIN",
    label: "FY8606  US OFFICE LIAISON ADMIN",
  },
  {
    value: "HEALTH SERVICE EVALUATION OFFICE",
    label: "HEALTH SERVICE EVALUATION OFFICE",
  },
  { value: "HHS", label: "HHS" },
  { value: "IBWC", label: "IBWC" },
  {
    value: "JOINT US MILITARY ADVISORY GROUP",
    label: "JOINT US MILITARY ADVISORY GROUP",
  },
  { value: "OFF ASST SEC ADM AND MGMT", label: "OFF ASST SEC ADM AND MGMT" },
  {
    value: "OFFICE OF ACQUISITION MGMT - MA",
    label: "OFFICE OF ACQUISITION MGMT - MA",
  },
  {
    value: "PROGRAM SUPPORT CENTER ACQ MGMT SVC",
    label: "PROGRAM SUPPORT CENTER ACQ MGMT SVC",
  },
  {
    value: "USSOCOM REGIONAL CONTRACTING OFFICE",
    label: "USSOCOM REGIONAL CONTRACTING OFFICE",
  },
  {
    value: "WASHINGTON HEADQUARTERS SERVICES",
    label: "WASHINGTON HEADQUARTERS SERVICES",
  },
  {
    value: "WHITE HOUSE COMMUNICATIONS AGENCY",
    label: "WHITE HOUSE COMMUNICATIONS AGENCY",
  },
  { value: "XU COMMANDER", label: "XU COMMANDER" },
];
